import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { format } from 'date-fns';
import Modal from '../../Modal';
import { DashboardFilter, FilterType, CreateFilterDTO, Widget, FilterOption } from '../types';
import { DashboardAPI } from '../api';
import { buildDraftFilterOverrides } from '../contexts/DashboardContext';
import { isRelativeDatetime, parseRelativeDatetime } from '../relativeDatetime';
import styles from './FilterEditor.module.scss';

interface FilterEditorProps {
  filters: DashboardFilter[];
  widgets: Widget[];
  dashboardId: string;
  filterValues?: Record<string, any>;
  onAdd: (dto: CreateFilterDTO) => void;
  onUpdate: (filterId: string, updates: Partial<DashboardFilter>) => void;
  onRemove: (filterId: string) => void;
  onClose: () => void;
  onSaveDraftFilters?: () => Promise<void>;
  onLoadFilterOptions?: () => Promise<void>;
  onReloadDashboard?: () => Promise<void>;
}

const FILTER_TYPE_OPTIONS: { value: FilterType; label: string }[] = [
  { value: 'select', label: 'Dropdown (Single)' },
  { value: 'multi_select', label: 'Dropdown (Multi)' },
  { value: 'text', label: 'Text Input' },
  { value: 'date_range', label: 'Date Range' },
];

const SCOPE_OPTIONS = [
  { value: '__dashboard__', label: 'All Widgets (Dashboard)' },
];

interface EditingFilter {
  id?: string;
  title: string;
  parameterName: string;
  filterType: FilterType;
  querySql: string;
  querySqlLoaded?: boolean;
  widgetId: string | null;
  defaultValue: any;
  config: Record<string, any>;
  order: number;
  hidden: boolean;
}

/** Build a claude.ai URL with a pre-filled prompt about the SQL error. */
function buildClaudeHelpUrl(sql: string, error: string): string {
  const prompt = [
    'I have a PostgreSQL query that produces an error. Help me understand and fix it.\n',
    '**Query:**',
    '```sql',
    sql.trim(),
    '```\n',
    '**Error:**',
    '```',
    error.trim(),
    '```\n',
    'Explain what causes this error and suggest a corrected query.',
  ].join('\n');
  return `https://claude.ai/new?q=${encodeURIComponent(prompt)}`;
}

const emptyFilter: EditingFilter = {
  title: '',
  parameterName: '',
  filterType: 'select',
  querySql: '',
  querySqlLoaded: true, // brand-new filter — SQL is whatever the admin types
  widgetId: null,
  defaultValue: null,
  config: {},
  order: 0,
  hidden: false,
};

const DATE_ISO_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Strict YYYY-MM-DD validator.
 *
 * `new Date("2024-02-31")` silently rolls over to March 2 in V8/SpiderMonkey,
 * so a naive `isNaN(getTime())` check accepts invalid calendar dates. We
 * instead check that the parsed components round-trip unchanged.
 */
function isValidIsoDate(raw: string): boolean {
  const match = raw.match(DATE_ISO_PATTERN);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() + 1 === month && d.getUTCDate() === day;
}

/** Validates a date_range boundary input: returns a preview / error hint or null when blank. */
function describeDateBoundary(value: string, t: (k: string, opts?: any) => string): { ok: boolean; text: string } | null {
  const raw = value.trim();
  if (!raw) return null;
  if (isRelativeDatetime(raw)) {
    const resolved = parseRelativeDatetime(raw);
    if (!resolved) {
      return { ok: false, text: t('Invalid relative expression (e.g. now, now-7d)') };
    }
    return { ok: true, text: `≈ ${format(resolved, 'yyyy-MM-dd')}` };
  }
  if (!isValidIsoDate(raw)) {
    return { ok: false, text: t('Use YYYY-MM-DD or a relative expression (now, now-7d, …)') };
  }
  return null;
}

export const FilterEditor: React.FC<FilterEditorProps> = ({
  filters,
  widgets,
  dashboardId,
  filterValues: parentFilterValues,
  onAdd,
  onUpdate,
  onRemove,
  onClose,
  onSaveDraftFilters,
  onLoadFilterOptions,
  onReloadDashboard,
}) => {
  const { t } = useTranslation();
  const [editingFilter, setEditingFilter] = useState<EditingFilter | null>(null);
  const [previewRows, setPreviewRows] = useState<FilterOption[] | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const filterSqlRef = useRef<HTMLTextAreaElement>(null);
  const originalEditingFilterRef = useRef<EditingFilter | null>(null);
  const pendingConfirmActionRef = useRef<'close' | 'cancel'>('close');

  useEffect(() => {
    const el = filterSqlRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 500) + 'px';
  }, [editingFilter?.querySql]);

  const insertAtCursor = useCallback((text: string) => {
    const textarea = filterSqlRef.current;
    if (!editingFilter) return;
    if (!textarea) {
      setEditingFilter({ ...editingFilter, querySql: editingFilter.querySql + text });
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const current = editingFilter.querySql;
    setEditingFilter({ ...editingFilter, querySql: current.substring(0, start) + text + current.substring(end) });
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
    });
  }, [editingFilter]);

  const savedWidgets = widgets.filter((w) => !w.id.startsWith('temp-'));
  const scopeOptions = [
    ...SCOPE_OPTIONS,
    ...savedWidgets.map((w) => ({ value: w.id, label: `Widget: ${w.title}` })),
  ];

  const handlePreviewValues = useCallback(async () => {
    if (!editingFilter?.querySql.trim()) {
      toast.warning(t('Enter a SQL query first') as string);
      return;
    }
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewRows(null);
    try {
      // Auto-save any unsaved draft filters so the backend knows about them
      if (onSaveDraftFilters) await onSaveDraftFilters();

      // Merge parent filter values with draft hidden filter defaults so unsaved
      // hidden filter edits are reflected in the preview. include_hidden_values
      // tells the backend to trust these overrides for admin edit mode.
      const effectiveFilterValues: Record<string, any> = { ...(parentFilterValues ?? {}) };
      filters.forEach((f) => {
        if (!f.hidden) return;
        if (effectiveFilterValues[f.parameterName] !== undefined) return;
        if (f.defaultValue != null) effectiveFilterValues[f.parameterName] = f.defaultValue;
      });

      // Send draft filter overrides so the backend uses unsaved filter SQL when
      // resolving cascading values / __all__ expansion of hidden allow_all filters.
      // Filters whose SQL was never lazy-loaded (empty querySql) are skipped so we
      // don't overwrite the backend's real stored SQL with an empty string. See
      // buildDraftFilterOverrides for details.
      const draftOverrides = buildDraftFilterOverrides(filters);

      const result = await DashboardAPI.executeRawQuery(
        dashboardId, editingFilter.querySql, effectiveFilterValues, undefined, undefined, true,
        draftOverrides,
      );
      if (result.error) {
        setPreviewError(result.error);
      } else {
        const rows = result.rows.map((row) => {
          const mapped: FilterOption = {};
          result.columns.forEach((col) => {
            mapped[col] = row[col] ?? null;
          });
          return mapped;
        });
        setPreviewRows(rows);
      }
    } catch (err: any) {
      setPreviewError(err?.message ?? t('Failed to execute query'));
    } finally {
      setPreviewLoading(false);
    }
  }, [editingFilter?.querySql, dashboardId, parentFilterValues, t, onSaveDraftFilters, filters]);

  const clearPreview = useCallback(() => {
    setPreviewRows(null);
    setPreviewError(null);
  }, []);

  const handleAddNew = (widgetId: string | null = null) => {
    clearPreview();
    const dashboardFilters = filters.filter((f) => f.widgetId == null);
    const widgetFiltersForWidget = widgetId ? filters.filter((f) => f.widgetId === widgetId) : [];
    const autoOrder = widgetId
      ? widgetFiltersForWidget.length
      : dashboardFilters.length;
    const newFilter = { ...emptyFilter, widgetId, order: autoOrder };
    setEditingFilter(newFilter);
    originalEditingFilterRef.current = { ...newFilter };
  };

  const handleEditExisting = async (filter: DashboardFilter) => {
    clearPreview();
    // Start with what we have locally
    const initial: EditingFilter = {
      id: filter.id,
      title: filter.title,
      parameterName: filter.parameterName,
      filterType: filter.filterType,
      querySql: filter.querySql,
      querySqlLoaded: filter.querySqlLoaded,
      widgetId: filter.widgetId,
      defaultValue: filter.defaultValue,
      config: filter.config ?? {},
      order: filter.order,
      hidden: filter.hidden,
    };
    setEditingFilter(initial);
    originalEditingFilterRef.current = { ...initial };

    // For saved filters, lazy-fetch the full SQL only when the draft doesn't
    // already hold it (gated on the loaded flag, not emptiness, so a knowingly
    // cleared SQL isn't re-fetched) — otherwise we'd overwrite unsaved edits
    // with the server's pre-edit value.
    if (!filter.id.startsWith('temp-') && !filter.querySqlLoaded) {
      try {
        const detail = await DashboardAPI.getFilter(dashboardId, filter.id);
        setEditingFilter((prev) =>
          prev?.id === filter.id ? { ...prev, querySql: detail.querySql, querySqlLoaded: true } : prev
        );
        // Also update original so the async-fetched querySql isn't counted as a user change
        if (originalEditingFilterRef.current?.id === filter.id) {
          originalEditingFilterRef.current = { ...originalEditingFilterRef.current, querySql: detail.querySql, querySqlLoaded: true };
        }
        // Persist fetched querySql to draft so it's available on next open
        onUpdate(filter.id, { querySql: detail.querySql, querySqlLoaded: true });
      } catch {
        // Keep local values if fetch fails
      }
    }
  };

  const handleSaveFilter = useCallback(async () => {
    if (!editingFilter) return;

    if (!editingFilter.title.trim() || !editingFilter.parameterName.trim()) {
      toast.warning(t('Title and Parameter Name are required') as string);
      return;
    }

    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(editingFilter.parameterName)) {
      toast.warning(t('Parameter Name must be a valid identifier (letters, numbers, underscores)') as string);
      return;
    }

    // Check for duplicate parameter name
    const duplicate = filters.find(
      (f) => f.parameterName === editingFilter.parameterName && f.id !== editingFilter.id
    );
    if (duplicate) {
      toast.warning(t('A filter with parameter name "{{name}}" already exists', { name: editingFilter.parameterName }) as string);
      return;
    }

    // Date range filters must have valid from and to in default value.
    // Each boundary must be either YYYY-MM-DD or a valid relative expression.
    if (editingFilter.filterType === 'date_range') {
      const dv = editingFilter.defaultValue;
      if (!dv || typeof dv !== 'object' || !dv.from || !dv.to) {
        toast.warning(t('Date range filter requires a default value with "from" and "to" dates') as string);
        return;
      }
      for (const key of ['from', 'to'] as const) {
        const hint = describeDateBoundary(String(dv[key]), t);
        if (hint && !hint.ok) {
          toast.warning(t('Invalid date range default: {{error}}', { error: hint.text }) as string);
          return;
        }
      }
    }

    if (editingFilter.id) {
      onUpdate(editingFilter.id, {
        title: editingFilter.title,
        parameterName: editingFilter.parameterName,
        filterType: editingFilter.filterType,
        querySql: editingFilter.querySql,
        querySqlLoaded: editingFilter.querySqlLoaded,
        widgetId: editingFilter.widgetId,
        defaultValue: editingFilter.defaultValue,
        config: editingFilter.config,
        order: editingFilter.order,
        hidden: editingFilter.hidden,
      });
      setEditingFilter(null);
      toast.success(t('Filter updated') as string);
    } else {
      // Save directly to backend and reload dashboard to get real filter with options.
      try {
        await DashboardAPI.createFilter(dashboardId, {
          title: editingFilter.title,
          parameterName: editingFilter.parameterName,
          filterType: editingFilter.filterType,
          querySql: editingFilter.querySql,
          widgetId: editingFilter.widgetId,
          defaultValue: editingFilter.defaultValue,
          config: editingFilter.config,
          order: editingFilter.order,
          hidden: editingFilter.hidden,
        });
        setEditingFilter(null);
        toast.success(t('Filter added') as string);

        // Reload dashboard and filter options so the new filter appears with values
        if (onReloadDashboard) await onReloadDashboard();
        if (onLoadFilterOptions) await onLoadFilterOptions();
      } catch (err: any) {
        toast.error(err?.message ?? t('Failed to save filter'));
      }
    }
  }, [editingFilter, onUpdate, t, dashboardId, onReloadDashboard, onLoadFilterOptions]);

  const handleDeleteFilter = (filterId: string) => {
    if (window.confirm(t('Are you sure you want to delete this filter?'))) {
      onRemove(filterId);
    }
  };

  const checkHasChanges = useCallback((): boolean => {
    if (!editingFilter) return false;
    const orig = originalEditingFilterRef.current;
    return !orig ||
      editingFilter.title !== orig.title ||
      editingFilter.parameterName !== orig.parameterName ||
      editingFilter.filterType !== orig.filterType ||
      editingFilter.querySql !== orig.querySql ||
      editingFilter.widgetId !== orig.widgetId ||
      editingFilter.hidden !== orig.hidden ||
      JSON.stringify(editingFilter.defaultValue) !== JSON.stringify(orig.defaultValue) ||
      JSON.stringify(editingFilter.config) !== JSON.stringify(orig.config);
  }, [editingFilter]);

  const handleAttemptClose = useCallback(() => {
    if (!editingFilter) {
      onClose();
      return;
    }
    if (checkHasChanges()) {
      pendingConfirmActionRef.current = 'close';
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  }, [editingFilter, onClose, checkHasChanges]);

  const handleAttemptCancel = useCallback(() => {
    if (checkHasChanges()) {
      pendingConfirmActionRef.current = 'cancel';
      setShowCloseConfirm(true);
    } else {
      clearPreview();
      setEditingFilter(null);
    }
  }, [checkHasChanges, clearPreview]);

  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirm(false);
    if (pendingConfirmActionRef.current === 'cancel') {
      clearPreview();
      setEditingFilter(null);
    } else {
      // Auto-save in-progress filter edits to draft before closing
      if (editingFilter?.id) {
        onUpdate(editingFilter.id, {
          title: editingFilter.title,
          parameterName: editingFilter.parameterName,
          filterType: editingFilter.filterType,
          querySql: editingFilter.querySql,
          querySqlLoaded: editingFilter.querySqlLoaded,
          widgetId: editingFilter.widgetId,
          defaultValue: editingFilter.defaultValue,
          config: editingFilter.config,
          order: editingFilter.order,
          hidden: editingFilter.hidden,
        });
      }
      onClose();
    }
  }, [editingFilter, onUpdate, onClose, clearPreview]);

  const dashboardLevelFilters = [...filters]
    .filter((f) => f.widgetId == null)
    .sort((a, b) => a.order - b.order);
  const widgetLevelFilters = [...filters]
    .filter((f) => f.widgetId != null)
    .sort((a, b) => a.order - b.order);

  // Drag-n-drop state for dashboard filters reordering
  const dragIndexRef = useRef<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<{ index: number; position: 'before' | 'after' } | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    dragIndexRef.current = index;
    setDraggingIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const fromIndex = dragIndexRef.current;
    if (fromIndex == null || fromIndex === index) {
      setDropTarget(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const position = e.clientY < midY ? 'before' : 'after';
    setDropTarget({ index, position });
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const fromIndex = dragIndexRef.current;
    if (fromIndex == null || !dropTarget) {
      dragIndexRef.current = null;
      setDraggingIndex(null);
      setDropTarget(null);
      return;
    }
    let toIndex = dropTarget.index;
    if (dropTarget.position === 'after') toIndex += 1;
    if (fromIndex < toIndex) toIndex -= 1;
    if (fromIndex === toIndex) {
      dragIndexRef.current = null;
      setDraggingIndex(null);
      setDropTarget(null);
      return;
    }
    const reordered = [...dashboardLevelFilters];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    reordered.forEach((filter, idx) => {
      if (filter.order !== idx) {
        onUpdate(filter.id, { order: idx });
      }
    });
    dragIndexRef.current = null;
    setDraggingIndex(null);
    setDropTarget(null);
  }, [dashboardLevelFilters, onUpdate, dropTarget]);

  const handleDragEnd = useCallback(() => {
    dragIndexRef.current = null;
    setDraggingIndex(null);
    setDropTarget(null);
  }, []);

  const content = (
    <div className={styles.container}>
      {editingFilter ? (
        <div className={styles.form}>
          <div className={styles.formRow}>
            <div className="form-element">
              <label>{t('Title')}</label>
              <input
                type="text"
                value={editingFilter.title}
                onChange={(e) => setEditingFilter({ ...editingFilter, title: e.target.value })}
                placeholder={t('e.g., Region')}
              />
            </div>
            <div className="form-element">
              <label>{t('Parameter Name')}</label>
              <input
                type="text"
                value={editingFilter.parameterName}
                onChange={(e) => setEditingFilter({ ...editingFilter, parameterName: e.target.value })}
                placeholder={t('e.g., region')}
              />
              <small className={styles.hint}>
                {editingFilter.filterType === 'date_range' ? (
                  <>{t('Use')} <code>{'{{' + (editingFilter.parameterName || 'param') + '_from}}'}</code> {t('and')} <code>{'{{' + (editingFilter.parameterName || 'param') + '_to}}'}</code> {t('in widget SQL')}</>
                ) : (
                  <>{t('Use')} <code>{'{{' + (editingFilter.parameterName || 'param') + '}}'}</code> {t('in widget SQL')}</>
                )}
              </small>
            </div>
          </div>

          <div className={styles.formRow}>
            <div className="form-element">
              <label>{t('Filter Type')}</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={FILTER_TYPE_OPTIONS}
                value={FILTER_TYPE_OPTIONS.find((o) => o.value === editingFilter.filterType)}
                onChange={(opt) => {
                  if (!opt) return;
                  const updates: Partial<EditingFilter> = { filterType: opt.value };
                  // Set default value to current year for date_range
                  if (opt.value === 'date_range' && editingFilter.defaultValue == null) {
                    const year = new Date().getFullYear();
                    updates.defaultValue = { from: `${year}-01-01`, to: `${year}-12-31` };
                  }
                  setEditingFilter({ ...editingFilter, ...updates });
                }}
                isSearchable={false}
              />
            </div>
            <div className="form-element">
              <label>{t('Scope')}</label>
              <Select
                className="react-select-container"
                classNamePrefix="react-select"
                options={scopeOptions}
                value={
                  scopeOptions.find((o) =>
                    editingFilter.widgetId
                      ? o.value === editingFilter.widgetId
                      : o.value === '__dashboard__'
                  ) ?? scopeOptions[0]
                }
                onChange={(opt) => {
                  if (opt) {
                    setEditingFilter({
                      ...editingFilter,
                      widgetId: opt.value === '__dashboard__' ? null : opt.value,
                    });
                  }
                }}
                isSearchable={false}
              />
              {widgets.length > savedWidgets.length && (
                <small className={styles.hint}>
                  {t('Save the dashboard first to assign filters to newly added widgets.')}
                </small>
              )}
            </div>
          </div>

          <div className="form-element">
            <label>{t('Options SQL Query')}</label>
            <textarea
              ref={filterSqlRef}
              className={styles.sqlTextarea}
              value={editingFilter.querySql}
              onChange={(e) => setEditingFilter({ ...editingFilter, querySql: e.target.value })}
              placeholder={t('SELECT DISTINCT region FROM data ORDER BY region')}
              spellCheck={false}
            />
            <small className={styles.hint}>
              {t('SQL to populate filter options. First column = value, second column = label.')}
              {(() => {
                // Dashboard-level filters: can depend on earlier (by order) dashboard filters.
                // Widget-level filters: can depend on ANY dashboard-level filter.
                const isWidgetLevel = editingFilter.widgetId != null;
                const currentOrder = editingFilter.order;
                const availableParams = filters
                  .filter((f) => {
                    if (f.id === editingFilter.id) return false;
                    if (isWidgetLevel) {
                      // Widget filters can use any dashboard-level filter
                      return f.widgetId == null;
                    }
                    // Dashboard filters can only use earlier-ordered dashboard filters
                    return f.widgetId == null && f.order < currentOrder;
                  })
                  .sort((a, b) => a.order - b.order);
                if (availableParams.length === 0) return null;
                return (
                  <>
                    <br />
                    {t('Available filter variables:')}
                    {' '}
                    {availableParams.flatMap((f, i) => {
                      if (f.filterType === 'date_range') {
                        const fromP = `{{${f.parameterName}_from}}`;
                        const toP = `{{${f.parameterName}_to}}`;
                        return [
                          <span key={`${f.id}_from`}>
                            {i > 0 && ', '}
                            <code className={styles.clickableHint} title={t('Click to insert')} onClick={() => insertAtCursor(fromP)}>{fromP}</code>
                          </span>,
                          <span key={`${f.id}_to`}>
                            {', '}
                            <code className={styles.clickableHint} title={t('Click to insert')} onClick={() => insertAtCursor(toP)}>{toP}</code>
                          </span>,
                        ];
                      }
                      const placeholder = `{{${f.parameterName}}}`;
                      return [(
                        <span key={f.id}>
                          {i > 0 && ', '}
                          <code className={styles.clickableHint} title={t('Click to insert')} onClick={() => insertAtCursor(placeholder)}>{placeholder}</code>
                        </span>
                      )];
                    })}
                  </>
                );
              })()}
            </small>
            <button
              type="button"
              className={styles.previewBtn}
              onClick={handlePreviewValues}
              disabled={previewLoading || !editingFilter.querySql.trim()}
            >
              {previewLoading ? t('Running...') : t('Preview Values')}
            </button>
            {previewError && (
              <div className={styles.previewError}>
                {previewError}
                {editingFilter?.querySql && (
                  <a
                    href={buildClaudeHelpUrl(editingFilter.querySql, previewError)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.helpLink}
                  >
                    {t('Ask Claude to help fix this')} &rarr;
                  </a>
                )}
              </div>
            )}
            {previewRows && (
              <div className={styles.previewContainer}>
                <div className={styles.previewHeader}>
                  <span className={styles.previewCount}>
                    {previewRows.length} {t('row(s) returned')}
                  </span>
                  <button type="button" className={styles.previewClose} onClick={clearPreview}>
                    &#10005;
                  </button>
                </div>
                <div className={styles.previewTable}>
                  <table>
                    <thead>
                      <tr>
                        {previewRows.length > 0 &&
                          Object.keys(previewRows[0]).map((col) => (
                            <th key={col}>{col}</th>
                          ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.slice(0, 20).map((row, i) => (
                        <tr key={i}>
                          {Object.values(row).map((val, j) => (
                            <td key={j}>{val != null ? String(val) : <em>null</em>}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {previewRows.length > 20 && (
                    <div className={styles.previewTruncated}>
                      {t('Showing first 20 of {{count}} rows', { count: previewRows.length })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {editingFilter.filterType === 'date_range' ? (
            (() => {
              const dv = (editingFilter.defaultValue && typeof editingFilter.defaultValue === 'object')
                ? editingFilter.defaultValue as { from?: string; to?: string }
                : {};
              const fromVal = typeof dv.from === 'string' ? dv.from : '';
              const toVal = typeof dv.to === 'string' ? dv.to : '';
              const fromHint = describeDateBoundary(fromVal, t);
              const toHint = describeDateBoundary(toVal, t);
              const updateBoundary = (key: 'from' | 'to', raw: string) => {
                // Trim on change so stored values always match the validated form.
                // Neither ISO dates nor relative expressions contain whitespace.
                const normalized = raw.trim();
                const next = { ...dv, [key]: normalized };
                if (!normalized) delete next[key];
                const isEmpty = !next.from && !next.to;
                setEditingFilter({ ...editingFilter, defaultValue: isEmpty ? null : next });
              };
              return (
                <div className="form-element">
                  <label>{t('Default Value')}</label>
                  <div className={styles.dateRangeInputs}>
                    <div className={styles.dateRangeField}>
                      <span className={styles.dateRangeFieldLabel}>{t('From')}</span>
                      <input
                        type="text"
                        value={fromVal}
                        onChange={(e) => updateBoundary('from', e.target.value)}
                        placeholder={`${new Date().getFullYear()}-01-01 ${t('or')} now-7d`}
                      />
                      {fromHint && (
                        <small className={fromHint.ok ? styles.hint : styles.dateRangeError}>
                          {fromHint.text}
                        </small>
                      )}
                    </div>
                    <div className={styles.dateRangeField}>
                      <span className={styles.dateRangeFieldLabel}>{t('To')}</span>
                      <input
                        type="text"
                        value={toVal}
                        onChange={(e) => updateBoundary('to', e.target.value)}
                        placeholder={`${new Date().getFullYear()}-12-31 ${t('or')} now`}
                      />
                      {toHint && (
                        <small className={toHint.ok ? styles.hint : styles.dateRangeError}>
                          {toHint.text}
                        </small>
                      )}
                    </div>
                  </div>
                  <small className={styles.hint}>
                    {t('Required. Accepts YYYY-MM-DD or relative expressions:')}{' '}
                    <code>now</code>, <code>now-7d</code>, <code>now-1m</code>
                    {'. '}
                    {t('Units: s, mi, h, d, w, m, y (case-sensitive).')}
                  </small>
                </div>
              );
            })()
          ) : (
            <div className="form-element">
              <label>{t('Default Value')}</label>
              <input
                type="text"
                value={editingFilter.defaultValue != null ? (typeof editingFilter.defaultValue === 'object' ? JSON.stringify(editingFilter.defaultValue) : String(editingFilter.defaultValue)) : ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (!raw) {
                    setEditingFilter({ ...editingFilter, defaultValue: null });
                    return;
                  }
                  try {
                    const parsed = JSON.parse(raw);
                    setEditingFilter({ ...editingFilter, defaultValue: parsed });
                  } catch {
                    setEditingFilter({ ...editingFilter, defaultValue: raw });
                  }
                }}
                placeholder={editingFilter.filterType === 'multi_select' ? '["value1","value2"]' : t('e.g., default_value')}
              />
              <small className={styles.hint}>
                {t('Pre-selected value when dashboard loads. Use JSON for complex types (arrays, objects).')}
              </small>
            </div>
          )}

          <div className={styles.formRow}>
            <div className="form-element">
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={editingFilter.config?.allow_all ?? false}
                  onChange={(e) => setEditingFilter({
                    ...editingFilter,
                    config: { ...editingFilter.config, allow_all: e.target.checked },
                  })}
                />
                {t('Allow "All" option')}
              </label>
              <small className={styles.hint}>
                {t('Adds an "All" option that shows all values without filtering.')}
              </small>
            </div>
            <div className="form-element">
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={editingFilter.hidden}
                  onChange={(e) => setEditingFilter({ ...editingFilter, hidden: e.target.checked })}
                />
                {t('Hidden')}
              </label>
              <small className={styles.hint}>
                {t('Hidden filters are not shown in the UI but can be set via click actions.')}
              </small>
            </div>
          </div>

        </div>
      ) : (
        <>
          {filters.length === 0 ? (
            <div className={styles.emptyState}>
              <p>{t('No filters configured yet.')}</p>
              <p>{t('Add filters to let users interactively filter dashboard data.')}</p>
            </div>
          ) : (
            <>
              <div className={styles.sectionHeader}>{t('Dashboard Filters')}</div>
              <div className={styles.filterList}>
                {dashboardLevelFilters.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>{t('No dashboard-level filters.')}</p>
                  </div>
                ) : (
                  dashboardLevelFilters.map((filter, index) => {
                    const isDragging = draggingIndex === index;
                    const isDropBefore = dropTarget?.index === index && dropTarget?.position === 'before';
                    const isDropAfter = dropTarget?.index === index && dropTarget?.position === 'after';
                    const rowClass = [
                      styles.filterRow,
                      isDragging ? styles.filterRowDragging : '',
                      isDropBefore ? styles.filterRowDropBefore : '',
                      isDropAfter ? styles.filterRowDropAfter : '',
                    ].filter(Boolean).join(' ');
                    return (
                    <div
                      key={filter.id}
                      className={rowClass}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                    >
                      <div className={styles.dragHandle} title={t('Drag to reorder')}>&#9776;</div>
                      <div className={styles.filterInfo}>
                        <span className={styles.filterTitle}>{filter.title}</span>
                        <span className={styles.filterMeta}>
                          <code>{`{{${filter.parameterName}}}`}</code>
                          {' · '}
                          {FILTER_TYPE_OPTIONS.find((o) => o.value === filter.filterType)?.label ?? filter.filterType}
                          {' · '}
                          {t('All Widgets')}
                          {filter.hidden && <> · <em>{t('Hidden')}</em></>}
                        </span>
                      </div>
                      <div className={styles.filterActions}>
                        <button
                          className={styles.editBtn}
                          onClick={() => handleEditExisting(filter)}
                          title={t('Edit')}
                        >
                          &#9998;
                        </button>
                        <button
                          className={styles.deleteFilterBtn}
                          onClick={() => handleDeleteFilter(filter.id)}
                          title={t('Delete')}
                        >
                          &#10005;
                        </button>
                      </div>
                    </div>
                    );
                  })
                )}
              </div>

              <div className={styles.sectionHeader}>{t('Widget Filters')}</div>
              <div className={styles.filterList}>
                {widgetLevelFilters.length === 0 ? (
                  <div className={styles.emptyState}>
                    <p>{t('No widget-level filters.')}</p>
                  </div>
                ) : (
                  widgetLevelFilters.map((filter) => (
                    <div key={filter.id} className={styles.filterRow}>
                      <div className={styles.filterInfo}>
                        <span className={styles.filterTitle}>{filter.title}</span>
                        <span className={styles.filterMeta}>
                          <code>{`{{${filter.parameterName}}}`}</code>
                          {' · '}
                          {FILTER_TYPE_OPTIONS.find((o) => o.value === filter.filterType)?.label ?? filter.filterType}
                          {' · '}
                          {`Widget: ${widgets.find((w) => w.id === filter.widgetId)?.title ?? filter.widgetId}`}
                          {filter.hidden && <> · <em>{t('Hidden')}</em></>}
                        </span>
                      </div>
                      <div className={styles.filterActions}>
                        <button
                          className={styles.editBtn}
                          onClick={() => handleEditExisting(filter)}
                          title={t('Edit')}
                        >
                          &#9998;
                        </button>
                        <button
                          className={styles.deleteFilterBtn}
                          onClick={() => handleDeleteFilter(filter.id)}
                          title={t('Delete')}
                        >
                          &#10005;
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
          <button className="btn-primary" onClick={() => handleAddNew()}>
            + {t('Add Filter')}
          </button>
        </>
      )}
    </div>
  );

  return (
    <>
      <Modal
        isVisible={true}
        title={t('Manage Filters')}
        onClose={handleAttemptClose}
        doNotCloseOnOutsideClick={true}
        className={styles.modal}
        content={content}
        footer={
          editingFilter ? (
            <>
              <button className="new-button" onClick={handleSaveFilter}>
                {editingFilter.id ? t('Update Filter') : t('Add Filter')}
              </button>
              <button className="btn-reset" onClick={handleAttemptCancel}>
                {t('Cancel')}
              </button>
            </>
          ) : (
            <button className="btn-reset" onClick={handleAttemptClose}>
              {t('Close')}
            </button>
          )
        }
      />
      {showCloseConfirm && (
        <Modal
          isVisible={true}
          title={t('Changes not saved')}
          className={undefined}
          onClose={() => setShowCloseConfirm(false)}
          content={<p>{t('Your changes will be discarded')}</p>}
          footer={
            <>
              <button className="btn-reset" onClick={() => setShowCloseConfirm(false)}>
                {t('Keep editing')}
              </button>
              <button className="btn btn-danger" onClick={handleConfirmClose}>
                {t('Discard changes')}
              </button>
            </>
          }
        />
      )}
    </>
  );
};
